import * as React from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';

import { Async } from '../../../components/AsyncProvider';
import { Button } from '../../../components/Button/index';
import { Loading } from '../../../components/Loading';
import { ProjectList } from '../../../components/ProjectList';
import { IAsyncData, IProjectFilter, IProjectList } from '../../../models';
import { IApplicationState } from '../../../store/models';
import { Container } from '../../Layout/components/Container';
import { IProject } from '../models';
import { getFilters, getProjects } from '../store/actions';
import { getProjects as getProjectsService } from '../store/services';
import { FilterTabs } from './FilterTabs/index';

interface IPagination {
    current: number;
    nextUrl: string | null;
}

export const Projects: React.FC = () => {
    const dispatch = useDispatch();
    const [pagination, setPagination] = React.useState<IPagination>({ current: 1, nextUrl: null });
    const [filter, setFilter] = React.useState<IProjectFilter | null>(null);
    const [projects, setProjects] = React.useState<IProject[]>([]);

    const filterList = useSelector<IApplicationState, IAsyncData<IProjectFilter[]>>((state) => state.projects.filter);
    const projectsListBranch = useSelector<IApplicationState, IAsyncData<IProjectList>>((state) => state.projects.projects);

    const dispatchProjects = React.useCallback(
        (currentPagination: IPagination, activeFilter: IProjectFilter | null) => {
            dispatch(getProjects(activeFilter, currentPagination.current)).payload
                .then(({ data }) => {
                    setProjects([...data.list]);
                    setPagination({ ...currentPagination, nextUrl: data.links.next });
                });
        },
        [dispatch],
    );

    React.useEffect(
        () => {
            dispatchProjects(pagination, filter);
            dispatch(getFilters());
        },
        // eslint-disable-next-line
        [dispatch]
    );

    const handleMoreClick = React.useCallback(
        async () => {
            const { data: { list, links: { next } } } = await getProjectsService(filter, pagination.current + 1);

            setProjects((prevProjects) => [...prevProjects, ...list]);
            setPagination({ nextUrl: next, current: pagination.current + 1 });
        },
        [pagination, filter, setPagination],
    );

    const handleFilterChange = React.useCallback(
        (activeFilter: IProjectFilter | null) => {
            setFilter(activeFilter);
            setPagination({ ...pagination, current: 1 });
            dispatchProjects({ ...pagination, current: 1 }, activeFilter);
        },
        [pagination, dispatchProjects],
    );

    return (
        <Container>
            <Helmet>
                <title>SCOUT - Projects</title>
            </Helmet>
            <Async
                branch={filterList}
                deps={[filterList, filter, setFilter, handleFilterChange, pagination]}
                successRender={(data) => (
                    <FilterTabs
                        filters={data}
                        onActiveFilterChange={handleFilterChange}
                    />
                )}
            />
            <Async
                branch={projectsListBranch}
                deps={[projectsListBranch, filter, pagination, setFilter, handleMoreClick, projects]}
                loadingRender={() => <Loading />}
                successRender={(data) => {
                    return (
                        <>
                            <ProjectList projects={projects} />
                            {
                                pagination.nextUrl && (
                                    <div className="show_more">
                                        <Button onClick={handleMoreClick}>
                                            More projects
                                        </Button>
                                    </div>
                                )
                            }
                        </>
                    );
                }}
            />
        </Container>
    );
};
